import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Send } from "lucide-react";
import { FollowupPicker, isTodayOrPast } from "@/components/FollowupPicker";

const TYPES = ["Call", "WhatsApp", "Meeting", "Internal"];
const QUICK = [
  { label: "Tomorrow", days: 1 },
  { label: "+2 days", days: 2 },
  { label: "+3 days", days: 3 },
];

function addDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export function NoteComposer({ recordId, onDone, focusSignal, consulted = false, followUpDate = null }) {
  const [type, setType] = useState("Call");
  const [text, setText] = useState("");
  const [fu, setFu] = useState("");
  const [when, setWhen] = useState("");
  const [askFu, setAskFu] = useState(false); // consulted lead with follow-up today/past → "When's the next follow-up?"
  const taRef = React.useRef(null);
  React.useEffect(() => { if (focusSignal) { setType("Call"); taRef.current?.focus(); taRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } }, [focusSignal]);
  const minWhen = addDays(-30); const maxWhen = addDays(0);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    if (consulted && !fu && (!followUpDate || isTodayOrPast(followUpDate))) { setAskFu(true); return; }
    setSaving(true);
    try {
      await api.post(`/cases/${recordId}/note`, { type, text, follow_up_date: fu || null, happened_on: when || null });
      toast.success(`${type} note added${TYPES.slice(0, 3).includes(type) ? " · Last Conversation updated" : ""}`);
      setText(""); setFu(""); setWhen(""); setAskFu(false);
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add note"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 shadow-sm" data-testid="note-composer">
      {askFu && (
        <div className="mb-2 rounded-xl border border-[#CA1212]/40 bg-[#CA1212]/5 p-2.5 flex flex-wrap items-center gap-2" data-testid="note-ask-followup">
          <span className="text-xs font-medium text-[#CA1212]">When's the next follow-up?</span>
          <FollowupPicker value={fu} onChange={(v) => { setFu(v); }} idPrefix="note-ask-fu" />
          <button onClick={submit} disabled={!fu || saving} data-testid="note-ask-fu-confirm" className="text-xs bg-[#FF4802] text-white px-3 py-1 rounded-lg disabled:opacity-50">Save note</button>
        </div>
      )}
      <div className="flex gap-1.5 mb-2">
        {TYPES.map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`note-type-${t.toLowerCase()}`}
            className={`px-3 py-1 rounded-full text-xs border font-medium transition-colors ${type === t ? "bg-[#FF4802] text-white border-[#FF4802]" : "bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#FF6F38]"}`}>{t}</button>
        ))}
      </div>
      <Textarea ref={taRef} value={text} onChange={(e) => setText(e.target.value)} placeholder="Log a conversation…" data-testid="note-text" className="text-sm border-[#E2E2E2] rounded-lg mb-2" />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-[#6B7280]">Follow-up:</span>
          {QUICK.map((q) => (
            <button key={q.label} onClick={() => setFu(addDays(q.days))} data-testid={`note-fu-${q.days}`}
              className={`px-2 py-0.5 rounded-full text-[11px] border ${fu === addDays(q.days) ? "bg-[#FF4802] text-white border-[#FF4802]" : "bg-white text-[#6B7280] border-[#E2E2E2]"}`}>{q.label}</button>
          ))}
          <input type="date" value={fu} onChange={(e) => setFu(e.target.value)} data-testid="note-fu-date" className="text-[11px] border border-[#E2E2E2] rounded-md px-1.5 py-0.5" />
          {fu && <button onClick={() => setFu("")} className="text-[11px] text-[#CA1212]">clear</button>}
          <span className="text-[11px] text-[#6B7280] ml-3">When did this happen?</span>
          <input type="date" value={when} min={minWhen} max={maxWhen} onChange={(e) => setWhen(e.target.value)} data-testid="note-when" className="text-[11px] border border-[#E2E2E2] rounded-md px-1.5 py-0.5" title="Default: now. Up to 30 days back." />
        </div>
        <button onClick={submit} disabled={saving} data-testid="note-submit" className="flex items-center gap-1.5 text-sm bg-[#FF4802] text-white px-4 py-1.5 rounded-lg hover:bg-[#E2521B] disabled:opacity-50 font-medium">
          <Send className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Add note"}
        </button>
      </div>
    </div>
  );
}
